import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { subscribeWithSelector } from 'zustand/middleware'
import type { StateCreator } from 'zustand'
import {
  createBaseSlice,
  type BaseSlice,
  type ExportResult,
} from '../../comp_reusable/report_page_universal/stores/baseReportStore'
import type { ScenarioReport } from '../../../../api/generated/models/scenarioReport'
import type { AnyReport } from '../../comp_reusable/report_page_universal/stores/baseReportStore'
import {
  saveNewScenarioReport,
  updateScenarioReport,
} from '../../../../api_operations/saveScenarioReport'
import washReportDocument from '../../../../utils/HackyExecutiveSummaryWashing'
import { toPercentage } from '../../../../utils/helper'

// Export status interface - now imported from base store
// export interface ExportStatus {
//   show: boolean;
//   success: boolean;
//   opacity: number;
// }

// ---- Scenario Report Slice ----
export type ScenarioReportSlice = {
  // Save-related state
  savingState: boolean

  resetAllStoreData: () => void

  // Comprehensive initialization function that handles all component setup
  initializeFromNavigation: (params: {
    data?: AnyReport
    routedReportId?: string
    pushToast?: any
  }) => Promise<void>

  // API operations - split export into data fetching and UI handling
  exportReportData: (
    format: string,
    onProgress?: (progress: number) => void,
  ) => Promise<ExportResult>

  handleExport: (
    formats: string[],
    params: {
      pushToast: any
      setProgress: (progress: number) => void
    },
  ) => Promise<void>

  handleSaveReportBtn: (pushToast: any) => Promise<void>

  handleUpdateReport: (reportId: number) => Promise<boolean>

  handleSaveNewReport: () => Promise<boolean>
}

export const createScenarioReportSlice: StateCreator<
  ScenarioReportStore,
  [['zustand/immer', never]],
  [],
  ScenarioReportSlice
> = (set, get) => ({
  savingState: false,

  // Reset component data
  resetAllStoreData: () => {
    // Reset common fields using base store function
    get().resetCommonStoreData()

    // Reset scenario-specific state
    set((state) => {
      state.savingState = false
    })
  },

  // Comprehensive initialization function that handles all component setup
  initializeFromNavigation: async (params: {
    data?: AnyReport
    routedReportId?: string
    pushToast?: any
  }) => {
    const { data, routedReportId, pushToast } = params

    // Convert string ID from URL params to number for store (API expects number)
    const routedReportIdNum = routedReportId
      ? parseInt(routedReportId, 10)
      : undefined

    // Initialize component data
    set((state) => {
      state.routedReportId = routedReportIdNum
    })

    // Set unsaved changes state based on whether we have a routedReportId
    get().setHasUnsavedChanges(!routedReportId)

    // Load report data based on whether we have an ID
    if (routedReportId) {
      // Fetch report from server using base store utility
      await get().fetchReportFromServer(
        '/api/scenarios/reports',
        routedReportId,
        pushToast,
      )
    } else if (data) {
      // Use data passed through navigation state
      get().initialize(data)
    }
  },

  // API operations - split export into data fetching part
  exportReportData: async (
    format: string,
    onProgress?: (progress: number) => void,
  ) => {
    const state = get()
    const title = state.getTitle()
    const anyReport = state.anyReport
    const reportDocument = state.getReportDocument()
    const hasAIPlus = state.getAIPlus()

    if (!anyReport || !reportDocument) {
      return { success: false, error: 'No report data available' }
    }

    try {
      // Build scenario-specific export data with proper typing
      const scenarioReport = anyReport as any // Cast to any since scenario field is not properly typed in ScenarioReport

      const scenario_highlights = [
        {
          title: 'Baseline',
          value: `${scenarioReport.scenario?.base_scenario?.predicted_model_end_value?.toFixed(3) || '0.000'}`,
          diff: null,
        },
        ...(scenarioReport.scenario?.scenarios?.map((elem: any) => {
          return {
            title: `${elem.name}`,
            value: `${elem.predicted_model_end_value?.toFixed(3) || '0.000'}`,
            diff: `${(((elem.predicted_model_end_value - scenarioReport.scenario.original_model_end_value) / scenarioReport.scenario.original_model_end_value) * 100).toFixed(1)}`,
          }
        }) || []),
      ]

      const driverMap = new Map()
      scenarioReport.scenario?.scenarios?.forEach((scenario: any) => {
        scenario.drivers?.forEach((driver: any) => {
          if (driver.original_end_value !== driver.mutated_end_value) {
            if (!driverMap.has(driver.driver_id)) {
              driverMap.set(driver.driver_id, {
                id: driver.driver_id,
                name: driver.name,
                source: driver.source,
                units: driver.units,
                original_end_value: driver.original_end_value,
              })
            }
          }
        })
      })

      const uniqueDrivers = Array.from(driverMap.values())

      const driver_simulation_values = uniqueDrivers.map((driver) => {
        const driverRow: any = {
          id: driver.id,
          driver: driver.name,
          source: driver.source,
          units: driver.units,
          unadjusted:
            driver.units === 'percentage'
              ? toPercentage(driver.original_end_value)
              : Math.round(driver.original_end_value),
        }

        // Add scenario-specific mutated values
        scenarioReport.scenario?.scenarios?.forEach((scenario: any) => {
          const driverValue = scenario.drivers?.find(
            (d: any) => d.driver_id === driver.id,
          )
          driverRow[`scenario_${scenario.id}`] =
            driverValue?.units === 'percentage'
              ? toPercentage(driverValue?.mutated_end_value)
              : Math.round(driverValue?.mutated_end_value) || null
        })

        return driverRow
      })

      const washedReportDocument = hasAIPlus
        ? washReportDocument(reportDocument)
        : reportDocument
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/scenarios/export_report?q=${format}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: title,
            user: state.getUserName(),
            fit_score: anyReport.model_fit_score,
            health_score: anyReport.model_health_score,
            executive_summary: {
              ...washedReportDocument,
              scenario_highlights,
              driver_simulation_values,
            },
          }),
        },
      )

      if (!response.ok) {
        return {
          success: false,
          error: `Failed to download: ${response.status}`,
        }
      }

      // Use base store utility for stream processing
      const blob = await get().processDownloadStream(response, onProgress)
      return {
        success: true,
        blob,
        filename: `${title}.${format}`,
      }
    } catch (error) {
      return { success: false, error: (error as Error).message }
    }
  },

  handleExport: async (formats, params) => {
    const state = get()

    // Use base store implementation with scenario-specific export data function
    await state.baseHandleExport(formats, {
      ...params,
      exportReportData: state.exportReportData,
    })
  },

  handleSaveReportBtn: async (pushToast) => {
    const state = get()

    // Use base store template with scenario-specific save functions
    await state.handleSaveReportTemplate({
      updateFunction: (reportId: number) => state.handleUpdateReport(reportId),
      saveNewFunction: () => state.handleSaveNewReport(),
      pushToast,
      setSavingState: (key, value) => {
        set((stateObj) => {
          stateObj.savingState = value
        })
      },
    })
  },

  handleUpdateReport: async (reportId: number) => {
    const state = get()

    // Get all data from store
    const reportDocument = state.getReportDocument()

    if (!state.anyReport) {
      console.error('🔍 [DEBUG] No report data available for update')
      return false
    }

    const reportData: any = {
      ...state.anyReport,
      executive_summary: reportDocument,
      project_solution_id: state.anyReport.project_solution?.id,
      client_id: state.anyReport.client?.id,
      artifact_id: state.anyReport.artifact?.id,
    }
    const result = await updateScenarioReport(reportId, reportData)

    if (result.success) {
      get().setHasUnsavedChanges(false)
      return true
    }

    return false
  },

  handleSaveNewReport: async () => {
    const state = get()

    // Get all data from store
    const reportDocument = state.getReportDocument()

    if (!state.anyReport) {
      console.error('🔍 [DEBUG] No report data available for saving')
      return false
    }

    const reportData: any = {
      ...state.anyReport,

      executive_summary: reportDocument,
      project_solution_id: state.anyReport.project_solution?.id,
      client_id: state.anyReport.client?.id,
      artifact_id: state.anyReport.artifact?.id,
    }
    const result = await saveNewScenarioReport(reportData)

    if (result.success) {
      const isNewIdAssigned =
        !state.freshReportId && result.data && result.data.id

      if (isNewIdAssigned) {
        set((state) => {
          state.freshReportId = result.data.id
        })
      }

      get().setHasUnsavedChanges(false)

      return true
    }

    return false
  },
})

// ---- Combined store type ----
export type ScenarioReportStore = BaseSlice & ScenarioReportSlice

export const useScenarioReportStore = create<ScenarioReportStore>()(
  subscribeWithSelector(
    immer((set, get, api) => {
      const baseSlice = createBaseSlice(set as any, get as any, api as any)
      const scenarioSlice = createScenarioReportSlice(set, get, api)

      return {
        ...baseSlice,
        ...scenarioSlice,
      }
    }),
  ),
)

// Helper hook to get the scenario data from anyReport with type safety
export const useScenarioFromReport = () => {
  return useScenarioReportStore((state) => {
    const report = state.anyReport as ScenarioReport | null
    return report?.scenario || null
  })
}
